import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Heart, Eye, ShoppingBag, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (price, currency = 'IDR') => {
  if (currency === 'IDR') return `Rp ${Number(price).toLocaleString('id-ID')}`;
  return `${currency} ${Number(price).toLocaleString()}`;
};

function isNearExpiry(expiry_date) {
  if (!expiry_date) return false;
  try {
    const exp = new Date(expiry_date);
    const now = new Date();
    const diffMs = exp - now;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    return diffDays > 0 && diffDays <= 90;
  } catch { return false; }
}

export default function ProductCard({ listing, onAuthRequired, currentUser, onWishlistChange }) {
  const { listing_id, photos, brand, product_name, price, currency, condition, seller, sold_out, expiry_date, size_value, size_unit } = listing;
  const { isAuthenticated, user } = useAuth();
  const [wishlisted, setWishlisted] = useState(false);
  const [buyingNow, setBuyingNow] = useState(false);
  const navigate = useNavigate();
  const isOwnListing = isAuthenticated && user?.user_id === listing.user_id;
  const showBuyAction = !isOwnListing && !listing.is_demo && !sold_out;

  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API}/wishlist/check/${listing_id}`)
        .then(r => setWishlisted(r.data.wishlisted))
        .catch(() => {});
    }
  }, [isAuthenticated, listing_id]);

  const handleWishlist = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      if (onAuthRequired) onAuthRequired();
      else toast.error('Please sign in to wishlist items');
      return;
    }
    try {
      const res = await axios.post(`${API}/wishlist/${listing_id}`, {});
      setWishlisted(res.data.wishlisted);
      toast.success(res.data.message);
      if (!res.data.wishlisted && onWishlistChange) onWishlistChange(listing_id);
    } catch {
      toast.error('Failed to update wishlist');
    }
  };

  const goToDetail = () => {
    sessionStorage.setItem('homepage_scroll', window.scrollY.toString());
    navigate(`/listing/${listing_id}`);
  };

  const handleBuyNow = async (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      sessionStorage.setItem('pending_listing_action', JSON.stringify({ listingId: listing_id, action: 'buy_now' }));
      sessionStorage.setItem('auth_return_url', `/listing/${listing_id}`);
      if (onAuthRequired) onAuthRequired();
      return;
    }
    if (sold_out) return;
    if (user?.user_id === listing.user_id) {
      toast.error("You can't buy your own product");
      return;
    }
    setBuyingNow(true);
    try {
      await axios.post(`${API}/cart/add`, { listing_id, quantity: 1 });
      navigate('/cart');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'single_seller_cart') {
        const sellerName = detail.current_seller_name || 'another seller';
        const productName = listing?.product_name || 'this item';
        const ok = window.confirm(
          `You're already shopping with ${sellerName}.\n\n` +
          `• Tap OK to replace your cart with ${productName}.\n` +
          `• Tap Cancel and we'll tuck it into your wishlist for after your ${sellerName} order.`
        );
        if (!ok) {
          try {
            const { data: check } = await axios.get(`${API}/wishlist/check/${listing_id}`);
            if (!check?.wishlisted) {
              await axios.post(`${API}/wishlist/${listing_id}`, {});
              toast.success(`Tucked into your wishlist 💝 Finish your order with ${sellerName} first — we'll keep ${productName} safe for you.`);
            } else {
              toast.success(`Already in your wishlist 💝 Finish your order with ${sellerName} first — we'll be here when you're back.`);
            }
          } catch {
            toast(`Your ${sellerName} cart is safe. Come back for ${productName} after checkout!`);
          }
          setBuyingNow(false);
          return;
        }
        try {
          await axios.post(`${API}/cart/replace-with`, { listing_id, quantity: 1 });
          navigate('/cart');
          return;
        } catch (err2) {
          toast.error(err2.response?.data?.detail || 'Failed to replace cart');
        }
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to add to cart');
      }
    } finally {
      setBuyingNow(false);
    }
  };

  const nearExpiry = isNearExpiry(expiry_date);

  return (
    <div
      className="group bg-white rounded-3xl sm:rounded-3xl rounded-2xl p-2.5 sm:p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-cream-100/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(244,194,194,0.15)] cursor-pointer"
      onClick={goToDetail}
      data-testid={`product-card-${listing_id}`}
    >
      {/* Image */}
      <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-cream-50 aspect-square mb-2 sm:mb-3">
        <img
          src={photos?.[0] || 'https://via.placeholder.com/300'}
          alt={product_name}
          className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${sold_out && !listing.is_demo ? 'opacity-50 grayscale' : ''}`}
          loading="lazy"
        />
        {/* Top-left: condition badge + barcode */}
        <div className="absolute top-2 sm:top-3 left-2 sm:left-3 flex gap-1.5">
          <Badge className="bg-white/90 backdrop-blur-sm text-[#78716C] px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium border-0 shadow-sm">
            {condition}
          </Badge>
          {listing.barcode_verified && (
            <Badge className="bg-emerald-500/90 backdrop-blur-sm text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium border-0 shadow-sm flex items-center gap-0.5" data-testid={`barcode-badge-${listing_id}`}>
              <CheckCircle2 size={10} /> Scanned
            </Badge>
          )}
        </div>

        {/* Top-right: wishlist heart */}
        <button
          onClick={(e) => { e.stopPropagation(); handleWishlist(e); }}
          className={`absolute top-2 sm:top-3 right-2 sm:right-3 p-1.5 sm:p-2 rounded-full transition-all shadow-sm ${
            wishlisted
              ? 'bg-[#F4C2C2] text-[#E05A5A]'
              : 'bg-white/90 backdrop-blur-sm text-[#A89F91] hover:text-[#E05A5A] hover:bg-white'
          } ${isOwnListing || listing.is_demo ? 'hidden' : ''}`}
          data-testid={`wishlist-btn-${listing_id}`}
        >
          <Heart size={14} className={`sm:w-4 sm:h-4 ${wishlisted ? 'fill-current' : ''}`} />
        </button>

        {/* Bottom-right: quick buy icon (mobile only, sm+ uses text button below) */}
        {showBuyAction && (
          <button
            onClick={handleBuyNow}
            disabled={buyingNow}
            className="sm:hidden absolute bottom-2 right-2 w-8 h-8 bg-[#F4C2C2] text-[#2C2A29] rounded-full flex items-center justify-center shadow-md transition-all active:scale-95 hover:bg-[#ECAEAE] disabled:opacity-50"
            data-testid={`quick-buy-btn-${listing_id}`}
          >
            <ShoppingBag size={14} />
          </button>
        )}

        {/* Bottom-left: near-expiry badge (mobile only) */}
        {nearExpiry && (
          <div className="sm:hidden absolute bottom-2 left-2">
            <span className="bg-amber-500/85 backdrop-blur-sm text-white px-2 py-0.5 rounded-full text-[9px] font-body font-semibold flex items-center gap-0.5">
              <Clock size={8} /> Expiring soon
            </span>
          </div>
        )}

        {/* Sample overlay */}
        {listing.is_demo && (
          <div className="absolute inset-0 flex items-end justify-center pb-3 sm:pb-4 pointer-events-none" data-testid={`sample-overlay-${listing_id}`}>
            <span className="bg-amber-600/85 backdrop-blur-sm text-white px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-[10px] sm:text-xs font-body font-semibold tracking-wide">
              Sample listing
            </span>
          </div>
        )}

        {/* Sold out overlay */}
        {!listing.is_demo && sold_out && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-testid={`sold-out-badge-${listing_id}`}>
            <span className="bg-[#2C2A29]/80 backdrop-blur-sm text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-body font-semibold tracking-wide">
              Sold out!
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="space-y-0.5 sm:space-y-1.5">
        <p className="text-[10px] sm:text-xs font-body uppercase tracking-[0.15em] font-semibold text-[#A89F91]">{brand}</p>
        <h3 className="font-body font-semibold text-[#2C2A29] text-xs sm:text-sm leading-snug line-clamp-2 hover:text-[#D48585] transition-colors"
          data-testid={`product-title-${listing_id}`}>{product_name}</h3>
        <div className="flex items-baseline gap-1">
          <p className="font-heading text-sm sm:text-lg font-semibold text-[#2C2A29]">{formatPrice(price, currency)}</p>
          {size_value && size_unit && (
            <span className="hidden sm:inline text-xs font-body text-[#A89F91]" data-testid={`product-size-${listing_id}`}>/ {size_value}{size_unit}</span>
          )}
        </div>

        {/* Expiry — desktop only (mobile shows badge on image if near-expiry) */}
        {expiry_date && (
          <p className="hidden sm:block text-xs font-body text-[#A89F91]">Exp. {expiry_date}</p>
        )}

        {/* Action buttons — desktop only */}
        <div className="hidden sm:flex gap-2 pt-2">
          <button onClick={(e) => { e.stopPropagation(); goToDetail(); }}
            className={`${isOwnListing || listing.is_demo ? 'w-full' : 'flex-1'} border border-cream-100 text-[#2C2A29] rounded-full px-3 py-2 font-body text-xs font-semibold transition-all hover:border-[#F4C2C2] hover:bg-[#FFF5F5] flex items-center justify-center gap-1.5`}
            data-testid={`view-details-btn-${listing_id}`}>
            <Eye size={13} /> View Details
          </button>
          {!isOwnListing && !listing.is_demo && (
            sold_out ? (
              <span className="flex-1 text-center text-[#78716C] bg-cream-50 rounded-full px-3 py-2 font-body text-xs font-semibold border border-cream-100">
                Sold out
              </span>
            ) : (
              <button onClick={handleBuyNow} disabled={buyingNow}
                className="flex-1 bg-[#F4C2C2] text-[#2C2A29] rounded-full px-3 py-2 font-body text-xs font-semibold transition-all hover:bg-[#ECAEAE] hover:shadow-md disabled:opacity-50 flex items-center justify-center gap-1.5"
                data-testid={`buy-now-card-btn-${listing_id}`}>
                <ShoppingBag size={13} /> {buyingNow ? 'Adding...' : 'Buy Now'}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
